// Hooks del módulo invoicing — sin react-query, estado local + refetch manual
// (mismo patrón que expo-capture).

import { useCallback, useEffect, useState } from 'react';

import { invoicingApi, InvoicingApiError } from '../api';
import type {
  DashboardSnapshot,
  Invoice,
  InvoiceCreateIn,
  InvoiceListFilters,
  Payment,
  PaymentCreateIn,
} from '../types';

function errorToMessage(e: unknown): string {
  if (e instanceof InvoicingApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Error desconocido';
}

// ── List ────────────────────────────────────────────────────────────

export function useInvoices(filters: InvoiceListFilters = {}) {
  const [data, setData] = useState<Invoice[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filterKey = JSON.stringify(filters);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await invoicingApi.list(filters));
    } catch (e) {
      setError(errorToMessage(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}

// ── Create ──────────────────────────────────────────────────────────

export function useCreateInvoice() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async (data: InvoiceCreateIn) => {
    setLoading(true);
    setError(null);
    try {
      return await invoicingApi.create(data);
    } catch (e) {
      setError(errorToMessage(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { submit, loading, error };
}

// ── From-quote ──────────────────────────────────────────────────────

export function useCreateInvoiceFromQuote() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async (quoteId: string) => {
    setLoading(true);
    setError(null);
    try {
      return await invoicingApi.createFromQuote(quoteId);
    } catch (e) {
      setError(errorToMessage(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { submit, loading, error };
}

// ── Payments ────────────────────────────────────────────────────────

export function useInvoicePayments(invoiceId: string | null) {
  const [data, setData] = useState<Payment[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!invoiceId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setData(await invoicingApi.listPayments(invoiceId));
    } catch (e) {
      setError(errorToMessage(e));
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}

export function useCreatePayment(invoiceId: string | null) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async (data: PaymentCreateIn) => {
    if (!invoiceId) return null;
    setLoading(true);
    setError(null);
    try {
      return await invoicingApi.createPayment(invoiceId, data);
    } catch (e) {
      setError(errorToMessage(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  return { submit, loading, error };
}

// ── Dashboard ───────────────────────────────────────────────────────

export function useInvoicingDashboard() {
  const [data, setData] = useState<DashboardSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await invoicingApi.dashboard());
    } catch (e) {
      setError(errorToMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}
